import { Controller } from "@hotwired/stimulus"
import { getSharedPusher } from "../../lib/sharedPusher"
import { formatScore } from "../../components/util"

export const POLL_INTERVAL_MS = 75000
export const REFRESH_MIN_MS = 20000
export const REFRESH_JITTER_MS = 8000
export const FETCH_RETRY_MS = 1500

export default class extends Controller {
  static values = { url: String, pageUrl: String, key: String, channel: String, revision: String, complete: Boolean }
  static targets = ["rows", "myRows", "mine", "pending", "pagination", "previous", "next", "page", "updated", "empty"]

  connect() {
    this.connected = true
    this.dirty = false
    this.lastRefresh = 0
    this.onSignal = ({ revision } = {}) => {
      if (revision !== this.revisionValue) this.markDirty()
    }
    this.onSubscribe = () => this.markDirty()
    this.onVisibility = () => {
      if (!document.hidden) this.markDirty()
    }
    document.addEventListener("visibilitychange", this.onVisibility)
    this.observer = new IntersectionObserver((entries) => {
      this.inViewport = entries.some((entry) => entry.isIntersecting)
      if (this.inViewport && this.dirty) this.scheduleRefresh()
    })
    this.observer.observe(this.element)
    if (this.keyValue && window.Pusher) {
      this.pusher = getSharedPusher(this.keyValue)
      this.channel = this.pusher.subscribe(this.channelValue)
      this.channel.bind("refresh", this.onSignal)
      // Also catches changes missed before subscription or during a disconnect.
      this.channel.bind("pusher:subscription_succeeded", this.onSubscribe)
    }
    this.pollId = setInterval(() => {
      if (this.connected && !this.completeValue) this.markDirty()
    }, POLL_INTERVAL_MS)
  }

  disconnect() {
    this.connected = false
    clearTimeout(this.timer)
    clearInterval(this.pollId)
    this.abortController?.abort()
    this.observer?.disconnect()
    document.removeEventListener("visibilitychange", this.onVisibility)
    this.channel?.unbind("refresh", this.onSignal)
    this.channel?.unbind("pusher:subscription_succeeded", this.onSubscribe)
    this.pusher?.unsubscribe(this.channelValue)
  }

  requestRefresh(event) {
    event.preventDefault()
    this.lastRefresh = 0
    this.markDirty()
  }

  markDirty() {
    this.dirty = true
    this.scheduleRefresh()
  }

  visible() {
    return !document.hidden && this.inViewport && this.element.getClientRects().length > 0
  }

  scheduleRefresh() {
    if (!this.connected || !this.visible() || this.loading || this.timer) return
    // Bound request rate during sustained scoring and spread simultaneous viewers.
    const delay = Math.max(0, REFRESH_MIN_MS - (Date.now() - this.lastRefresh)) + Math.random() * REFRESH_JITTER_MS
    this.timer = setTimeout(() => this.refresh(), delay)
  }

  async refresh() {
    this.timer = null
    if (!this.connected || !this.visible()) return
    this.loading = true
    this.dirty = false
    this.lastRefresh = Date.now()
    this.abortController = new AbortController()
    let succeeded = false
    try {
      const response = await fetch(this.urlValue, {
        signal: this.abortController.signal,
        credentials: "same-origin",
        headers: { Accept: "application/json" }
      })
      if (!response.ok) throw new Error("Leaderboard refresh failed")
      const board = await response.json()
      if (!this.connected) return
      this.updateBoard(board)
      this.revisionValue = board.revision
      this.retriedFailure = false
      succeeded = true
    } catch (_) {
      this.dirty = true
      if (!this.retriedFailure) {
        this.retriedFailure = true
        this.timer = setTimeout(() => this.refresh(), FETCH_RETRY_MS)
      }
    } finally {
      this.loading = false
      if (succeeded && this.dirty) this.scheduleRefresh()
    }
  }

  updateBoard(board) {
    this.renderRows(this.rowsTarget, board.rows, board.published)
    this.renderRows(this.myRowsTarget, board.myRows, board.published)
    this.mineTarget.hidden = board.myRows.length === 0
    if (this.hasEmptyTarget) this.emptyTarget.hidden = board.rows.length > 0
    this.pendingTarget.hidden = !board.awaitingVenues
    this.completeValue = board.complete
    if (this.hasUpdatedTarget) {
      const now = new Date()
      const hour = now.getHours() % 12 || 12
      const minutes = now.getMinutes().toString().padStart(2, "0")
      this.updatedTarget.textContent = `Last updated ${hour}:${minutes}`
    }
    this.element.querySelectorAll("[data-network-points]").forEach((cell) => {
      cell.hidden = !board.published
    })
    this.paginationTarget.hidden = board.totalPages <= 1
    this.pageTarget.textContent = `Page ${board.page} of ${board.totalPages}`
    this.previousTarget.hidden = board.page <= 1
    this.nextTarget.hidden = board.page >= board.totalPages
    for (const [link, page] of [
      [this.previousTarget, board.page - 1],
      [this.nextTarget, board.page + 1]
    ]) {
      const url = new URL(this.pageUrlValue, window.location.origin)
      url.searchParams.set("page", page)
      link.href = url.toString()
    }
  }

  renderRows(body, rows, published) {
    body.replaceChildren(
      ...rows.flatMap((row) => {
        const tr = document.createElement("tr")
        tr.className = "team"
        for (const field of ["position", "name", "score", "thru", "points"]) {
          const td = document.createElement("td")
          td.textContent = row[field] ?? ""
          if (field === "score" && row.score != null) td.textContent = formatScore(Number(row.score))
          if (field === "name") {
            td.className = "team-name"
          } else if (field === "position") {
            td.style.minWidth = "26px"
          } else {
            td.className = "round-scoring-value"
          }
          if (field === "points") {
            td.dataset.networkPoints = ""
            td.hidden = !published
          }
          tr.append(td)
        }
        const venueRow = document.createElement("tr")
        venueRow.className = "players"
        venueRow.append(document.createElement("td"))
        const venue = document.createElement("td")
        venue.colSpan = published ? 4 : 3
        venue.textContent = row.community ?? ""
        venueRow.append(venue)
        return [tr, venueRow]
      })
    )
  }
}
